import React from 'react';
/**
 * Utility functions to generate mock data for different time periods
 * Updated to respect actual date ranges from filters
 */
// Helper function to generate dates between start and end date
const generateDateRange = (startDate: Date, endDate: Date, granularity: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly') => {
  const dates: {
    date: string;
    hour?: string;
  }[] = [];
  const current = new Date(startDate);
  if (granularity === 'hourly') {
    for (let hour = 0; hour < 24; hour++) {
      const date = new Date(current.getFullYear(), current.getMonth(), current.getDate(), hour);
      dates.push({
        date: date.toISOString(),
        hour: `${hour}:00`
      });
    }
  } else if (granularity === 'daily') {
    while (current <= endDate) {
      dates.push({
        date: current.toISOString().split('T')[0]
      });
      current.setDate(current.getDate() + 1);
    }
  } else if (granularity === 'weekly') {
    while (current <= endDate) {
      dates.push({
        date: current.toISOString().split('T')[0]
      });
      current.setDate(current.getDate() + 7);
    }
  } else if (granularity === 'monthly') {
    while (current <= endDate) {
      dates.push({
        date: current.toISOString().split('T')[0]
      });
      current.setMonth(current.getMonth() + 1);
    }
  } else if (granularity === 'quarterly') {
    // Align to quarter start
    const quarterMonth = Math.floor(current.getMonth() / 3) * 3;
    current.setMonth(quarterMonth, 1);
    while (current <= endDate) {
      dates.push({
        date: current.toISOString().split('T')[0]
      });
      current.setMonth(current.getMonth() + 3);
    }
  } else if (granularity === 'yearly') {
    current.setMonth(0, 1);
    while (current <= endDate) {
      dates.push({
        date: current.toISOString().split('T')[0]
      });
      current.setFullYear(current.getFullYear() + 1);
    }
  }
  return dates;
};
// Determine granularity based on date range
const determineGranularity = (startDate: Date, endDate: Date, overrideGranularity?: string): 'hourly' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' => {
  if (overrideGranularity && ['hourly', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'].includes(overrideGranularity)) {
    return overrideGranularity as 'hourly' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  }
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return 'hourly';
  if (diffDays <= 31) return 'daily';
  if (diffDays <= 90) return 'weekly';
  if (diffDays <= 365) return 'monthly';
  if (diffDays <= 730) return 'quarterly';
  return 'yearly';
};
// Generate hourly data for today
const generateTodayData = (baseAmount, baseCount) => {
  return Array.from({
    length: 24
  }, (_, i) => {
    // Create a pattern that simulates typical business hours with peaks
    let hourFactor = 0.5; // Base factor for off-hours
    // Morning peak (9am-11am)
    if (i >= 9 && i <= 11) {
      hourFactor = 1.5;
    }
    // Lunch hour dip (12pm)
    else if (i === 12) {
      hourFactor = 0.8;
    }
    // Afternoon peak (1pm-5pm)
    else if (i >= 13 && i <= 17) {
      hourFactor = 1.3;
    }
    // Evening activity (6pm-8pm)
    else if (i >= 18 && i <= 20) {
      hourFactor = 0.9;
    }
    // Night (after 8pm)
    else if (i > 20) {
      hourFactor = 0.3;
    }
    // Early morning (before 9am)
    else {
      hourFactor = 0.2;
    }
    // Add some randomness
    hourFactor *= 0.9 + Math.random() * 0.2;
    const now = new Date();
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), i);
    return {
      date: date.toISOString(),
      hour: `${i}:00`,
      amount: Math.floor(baseAmount * hourFactor),
      count: Math.floor(baseCount * hourFactor)
    };
  });
};
// Generate payment volume data for different time periods
// UPDATED: Now accepts optional startDate and endDate
export const generatePaymentVolumeData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  const hasValidDates = startDate instanceof Date && endDate instanceof Date && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime());
  if (hasValidDates) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map((dateInfo, i) => {
      // Create realistic patterns based on day of week
      const date = new Date(dateInfo.date);
      const dayOfWeek = date.getDay();
      let dayFactor = 1.0;
      // Weekend dip
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        dayFactor = 0.7;
      }
      // Mid-week peak
      else if (dayOfWeek === 2 || dayOfWeek === 3) {
        dayFactor = 1.2;
      }
      // Add trend and randomness
      const trendFactor = 1 + i / dateRange.length * 0.1; // Slight upward trend
      const randomFactor = 0.9 + Math.random() * 0.2;
      return {
        date: dateInfo.date,
        hour: dateInfo.hour,
        amount: Math.floor(14000000 * dayFactor * trendFactor * randomFactor)
      };
    });
  }
  console.log('⚠️ Falling back to legacy timeRange behavior');
  // Fallback to old behavior for backward compatibility
  if (timeRange === 'today') {
    return generateTodayData(750000, 30);
  } else if (timeRange === '7d') {
    const now = new Date();
    return Array.from({
      length: 7
    }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (6 - i));
      return {
        date: date.toISOString().split('T')[0],
        amount: 13500000 + Math.floor(Math.random() * 5000000)
      };
    });
  } else if (timeRange === '30d') {
    const now = new Date();
    return Array.from({
      length: 30
    }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (29 - i));
      return {
        date: date.toISOString().split('T')[0],
        amount: 12000000 + Math.floor(Math.random() * 9000000)
      };
    });
  } else {
    // 1y - Generate monthly data
    const now = new Date();
    return Array.from({
      length: 12
    }, (_, i) => {
      const date = new Date(now);
      date.setMonth(date.getMonth() - (11 - i));
      const seasonalFactor = date.getMonth() >= 9 || date.getMonth() <= 1 ? 1.2 : 1;
      return {
        date: date.toISOString().split('T')[0],
        amount: Math.floor((360000000 + Math.random() * 120000000) * seasonalFactor)
      };
    });
  }
};
// Generate previous period data for comparison
// FIXED: Now shifts dates back by the appropriate period length
export const generatePreviousPeriodData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  // Calculate how many days to shift back based on the period
  let daysToShift = 7; // Default for 7d
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    // For custom date ranges, calculate the period length
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  if (timeRange === 'today') {
    // Yesterday's data (slightly different pattern)
    return generateTodayData(690000, 28).map(item => {
      // Shift the date back by 1 day
      const date = new Date(item.date);
      date.setDate(date.getDate() - 1);
      return {
        ...item,
        date: date.toISOString(),
        isPrevious: true
      };
    });
  } else {
    // Previous period - shift all dates back
    return currentData.map(item => {
      const date = new Date(item.date);
      date.setDate(date.getDate() - daysToShift);
      return {
        ...item,
        date: date.toISOString().split('T')[0],
        amount: Math.floor(item.amount * (0.85 + Math.random() * 0.2)),
        isPrevious: true
      };
    });
  }
};
// Generate transaction count data for different time periods
// UPDATED: Now accepts optional startDate and endDate
export const generateTransactionCountData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  // CRITICAL FIX: Validate that startDate and endDate are actual Date objects
  const hasValidDates = startDate instanceof Date && endDate instanceof Date && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime());
  // If actual dates provided AND valid, use them
  if (hasValidDates) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map((dateInfo, i) => {
      const date = new Date(dateInfo.date);
      const dayOfWeek = date.getDay();
      let dayFactor = 1.0;
      // Weekend dip
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        dayFactor = 0.7;
      }
      // Mid-week peak
      else if (dayOfWeek === 2 || dayOfWeek === 3) {
        dayFactor = 1.2;
      }
      const trendFactor = 1 + i / dateRange.length * 0.1;
      const randomFactor = 0.9 + Math.random() * 0.2;
      return {
        date: dateInfo.date,
        hour: dateInfo.hour,
        count: Math.floor(550 * dayFactor * trendFactor * randomFactor)
      };
    });
  }
  // Fallback to old behavior
  if (timeRange === 'today') {
    return generateTodayData(750000, 30).map(({
      date,
      hour,
      count
    }) => ({
      date,
      hour,
      count
    }));
  } else if (timeRange === '7d') {
    const now = new Date();
    return Array.from({
      length: 7
    }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (6 - i));
      return {
        date: date.toISOString().split('T')[0],
        count: 500 + Math.floor(Math.random() * 150)
      };
    });
  } else if (timeRange === '30d') {
    const now = new Date();
    return Array.from({
      length: 30
    }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (29 - i));
      return {
        date: date.toISOString().split('T')[0],
        count: 450 + Math.floor(Math.random() * 250)
      };
    });
  } else {
    // 1y - Generate monthly data
    const now = new Date();
    return Array.from({
      length: 12
    }, (_, i) => {
      const date = new Date(now);
      date.setMonth(date.getMonth() - (11 - i));
      const seasonalFactor = date.getMonth() >= 9 || date.getMonth() <= 1 ? 1.2 : 1;
      return {
        date: date.toISOString().split('T')[0],
        count: Math.floor((15000 + Math.random() * 5000) * seasonalFactor)
      };
    });
  }
};
// Generate previous period transaction count data
// FIXED: Now shifts dates back by the appropriate period length
export const generatePreviousTransactionCountData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  // Calculate how many days to shift back based on the period
  let daysToShift = 7; // Default for 7d
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    // For custom date ranges, calculate the period length
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  if (timeRange === 'today') {
    // Yesterday's data
    return generateTodayData(690000, 28).map(({
      date,
      hour,
      count
    }) => {
      const dateObj = new Date(date);
      dateObj.setDate(dateObj.getDate() - 1);
      return {
        date: dateObj.toISOString(),
        hour,
        count,
        isPrevious: true
      };
    });
  } else {
    // Previous period - shift all dates back
    return currentData.map(item => {
      const date = new Date(item.date);
      date.setDate(date.getDate() - daysToShift);
      return {
        ...item,
        date: date.toISOString().split('T')[0],
        count: Math.floor(item.count * (0.85 + Math.random() * 0.2)),
        isPrevious: true
      };
    });
  }
};
// Generate average transaction data for different time periods
// UPDATED: Now accepts optional startDate and endDate
export const generateAverageTransactionData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  // CRITICAL FIX: Validate that startDate and endDate are actual Date objects
  const hasValidDates = startDate instanceof Date && endDate instanceof Date && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime());
  // If actual dates provided AND valid, use them
  if (hasValidDates) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map(dateInfo => ({
      date: dateInfo.date,
      hour: dateInfo.hour,
      amount: 24000 + Math.floor(Math.random() * 7500)
    }));
  }
  // Fallback to old behavior
  if (timeRange === 'today') {
    return generateTodayData(750000, 30).map(({
      date,
      hour,
      amount,
      count
    }) => ({
      date,
      hour,
      amount: Math.floor(amount / count)
    }));
  } else if (timeRange === '7d') {
    const now = new Date();
    return Array.from({
      length: 7
    }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (6 - i));
      return {
        date: date.toISOString().split('T')[0],
        amount: 25500 + Math.floor(Math.random() * 4000)
      };
    });
  } else if (timeRange === '30d') {
    const now = new Date();
    return Array.from({
      length: 30
    }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (29 - i));
      return {
        date: date.toISOString().split('T')[0],
        amount: 24000 + Math.floor(Math.random() * 7500)
      };
    });
  } else {
    // 1y - Generate monthly data
    const now = new Date();
    return Array.from({
      length: 12
    }, (_, i) => {
      const date = new Date(now);
      date.setMonth(date.getMonth() - (11 - i));
      return {
        date: date.toISOString().split('T')[0],
        amount: 25500 + Math.floor(Math.random() * 6000)
      };
    });
  }
};
// Generate refund data over time
// UPDATED: Now accepts optional startDate and endDate
export const generateRefundData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  // Base the refund data on a percentage of the payment volume
  const paymentData = generatePaymentVolumeData(timeRange, startDate, endDate, userGranularity);
  return paymentData.map(item => {
    // Refunds are typically 2-5% of transaction volume
    const refundPercentage = 0.02 + Math.random() * 0.03;
    return {
      date: item.date,
      hour: item.hour,
      amount: Math.round(item.amount * refundPercentage),
      count: Math.round(item.amount * refundPercentage / 25500)
    };
  });
};
// Generate previous period refund data
// FIXED: Now shifts dates back by the appropriate period length
export const generatePreviousRefundData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  // Calculate how many days to shift back based on the period
  let daysToShift = 7; // Default for 7d
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    // For custom date ranges, calculate the period length
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  // Previous period - shift all dates back
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      amount: Math.floor(item.amount * (0.9 + Math.random() * 0.2)),
      count: Math.floor(item.count * (0.9 + Math.random() * 0.2)),
      isPrevious: true
    };
  });
};
// Generate payment methods data
export const generatePaymentMethodsData = () => {
  return [{
    name: 'Card Payments',
    value: 42,
    color: '#6E9FD9'
  }, {
    name: 'Multi Currency Card Payments',
    value: 20,
    color: '#D16B5F'
  }, {
    name: 'DuitNow',
    value: 15,
    color: '#7FC29B'
  }, {
    name: 'FPX',
    value: 10,
    color: '#D9984B'
  }, {
    name: 'WechatPay',
    value: 7,
    color: '#9C7CB6'
  }, {
    name: 'UnionPay',
    value: 4,
    color: '#5DAFBA'
  }, {
    name: 'Cryptocurrency Payments',
    value: 2,
    color: '#8884d8'
  }];
};
export const generateConversionData = () => {
  return [{
    name: 'Initiated',
    value: 1000,
    color: '#6E9FD9'
  }, {
    name: 'Attempted',
    value: 850,
    color: '#D9984B'
  }, {
    name: 'Successful',
    value: 720,
    color: '#7FC29B'
  }];
};
export const generateAttemptsData = () => {
  return [{
    attempts: '1',
    count: 450
  }, {
    attempts: '2',
    count: 200
  }, {
    attempts: '3',
    count: 50
  }, {
    attempts: '4+',
    count: 20
  }];
};
export const generateGeoPaymentData = () => {
  return [{
    country: 'United States',
    countryCode: 'US',
    amount: 37350000,
    transactions: 1240,
    lat: 37.0902,
    lng: -95.7129
  }, {
    country: 'United Kingdom',
    countryCode: 'GB',
    amount: 23490000,
    transactions: 835,
    lat: 55.3781,
    lng: -3.436
  }, {
    country: 'Germany',
    countryCode: 'DE',
    amount: 17010000,
    transactions: 623,
    lat: 51.1657,
    lng: 10.4515
  }, {
    country: 'France',
    countryCode: 'FR',
    amount: 12960000,
    transactions: 476,
    lat: 46.2276,
    lng: 2.2137
  }, {
    country: 'Japan',
    countryCode: 'JP',
    amount: 11670000,
    transactions: 412,
    lat: 36.2048,
    lng: 138.2529
  }, {
    country: 'Australia',
    countryCode: 'AU',
    amount: 9750000,
    transactions: 345,
    lat: -25.2744,
    lng: 133.7751
  }, {
    country: 'Canada',
    countryCode: 'CA',
    amount: 8940000,
    transactions: 318,
    lat: 56.1304,
    lng: -106.3468
  }, {
    country: 'India',
    countryCode: 'IN',
    amount: 7380000,
    transactions: 287,
    lat: 20.5937,
    lng: 78.9629
  }, {
    country: 'Brazil',
    countryCode: 'BR',
    amount: 5940000,
    transactions: 213,
    lat: -14.235,
    lng: -51.9253
  }, {
    country: 'Singapore',
    countryCode: 'SG',
    amount: 5250000,
    transactions: 186,
    lat: 1.3521,
    lng: 103.8198
  }, {
    country: 'Spain',
    countryCode: 'ES',
    amount: 4590000,
    transactions: 168,
    lat: 40.4637,
    lng: -3.7492
  }, {
    country: 'Netherlands',
    countryCode: 'NL',
    amount: 4260000,
    transactions: 156,
    lat: 52.1326,
    lng: 5.2913
  }, {
    country: 'South Korea',
    countryCode: 'KR',
    amount: 3840000,
    transactions: 142,
    lat: 35.9078,
    lng: 127.7669
  }, {
    country: 'Italy',
    countryCode: 'IT',
    amount: 3450000,
    transactions: 127,
    lat: 41.8719,
    lng: 12.5674
  }, {
    country: 'Hong Kong',
    countryCode: 'HK',
    amount: 2940000,
    transactions: 108,
    lat: 22.3193,
    lng: 114.1694
  }];
};
export function generateCompanies() {
  return [{
    id: 'comp-1',
    name: 'Nusantara Grocers Sdn Bhd'
  }, {
    id: 'comp-2',
    name: 'Klang Valley Supermart Sdn Bhd'
  }, {
    id: 'comp-3',
    name: 'Penang Heritage Foods Sdn Bhd'
  }, {
    id: 'comp-4',
    name: 'Borneo Outdoor Adventures Sdn Bhd'
  }, {
    id: 'comp-5',
    name: 'Straits Fashion House Sdn Bhd'
  }, {
    id: 'comp-6',
    name: 'Kota Kinabalu Duty Free Sdn Bhd'
  }];
}
export const generatePendingRefundsData = () => {
  const count = 5 + Math.floor(Math.random() * 11);
  const pendingRefunds = [];
  for (let i = 0; i < count; i++) {
    pendingRefunds.push({
      id: `ref-${i + 1}`,
      customerId: `cust-${100 + Math.floor(Math.random() * 900)}`,
      amount: 7500 + Math.floor(Math.random() * 142500),
      date: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      reason: ['Customer request', 'Product defect', 'Wrong item', 'Late delivery', 'Other'][Math.floor(Math.random() * 5)]
    });
  }
  return pendingRefunds;
};
export const generateRefundReasonsData = () => {
  return [{
    reason: 'Customer request',
    count: 42,
    color: '#6E9FD9'
  }, {
    reason: 'Product defect',
    count: 28,
    color: '#D16B5F'
  }, {
    reason: 'Wrong item',
    count: 15,
    color: '#7FC29B'
  }, {
    reason: 'Late delivery',
    count: 10,
    color: '#D9984B'
  }, {
    reason: 'Other',
    count: 5,
    color: '#9C7CB6'
  }];
};
export function filterDataByCompanies(data, selectedCompanyIds) {
  console.group('🔧 filterDataByCompanies');
  console.log('📥 Input data:', data);
  console.log('🏢 Selected company IDs:', selectedCompanyIds);
  if (selectedCompanyIds.length === 0) {
    console.log('✅ No filtering needed (empty selection)');
    console.groupEnd();
    return data;
  }
  const filterRatio = selectedCompanyIds.length / 8;
  console.log('📊 Filter ratio:', filterRatio);
  const result = data.map((item, index) => {
    const filtered = {
      ...item
    };
    console.log(`   Item ${index}:`, item);
    if (typeof item.amount === 'number') {
      filtered.amount = Math.round(item.amount * filterRatio);
      console.log(`   ✓ Filtered amount: ${item.amount} → ${filtered.amount}`);
    }
    if (typeof item.count === 'number') {
      filtered.count = Math.round(item.count * filterRatio);
      console.log(`   ✓ Filtered count: ${item.count} → ${filtered.count}`);
    }
    if (typeof item.value === 'number') {
      filtered.value = Math.round(item.value * filterRatio);
      console.log(`   ✓ Filtered value: ${item.value} → ${filtered.value}`);
    }
    console.log(`   ✅ Result:`, filtered);
    return filtered;
  });
  console.log('📤 Output data:', result);
  console.groupEnd();
  return result;
}
// Generate dispute count data
export const generateDisputeCountData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  if (startDate && endDate) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map(dateInfo => ({
      date: dateInfo.date,
      hour: dateInfo.hour,
      count: Math.floor(Math.random() * 10) + 3
    }));
  }
  const paymentData = generatePaymentVolumeData(timeRange, startDate, endDate, userGranularity);
  return paymentData.map(item => ({
    date: item.date,
    hour: item.hour,
    count: Math.floor(Math.random() * 10) + 3
  }));
};
// Generate previous dispute count data
export const generatePreviousDisputeCountData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  let daysToShift = 7;
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      count: Math.floor(item.count * (0.85 + Math.random() * 0.2)),
      isPrevious: true
    };
  });
};
// Generate dispute activity rate data
export const generateDisputeActivityData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  if (startDate && endDate) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map(dateInfo => ({
      date: dateInfo.date,
      hour: dateInfo.hour,
      rate: parseFloat((1.0 + Math.random() * 0.5).toFixed(2))
    }));
  }
  const paymentData = generatePaymentVolumeData(timeRange, startDate, endDate, userGranularity);
  return paymentData.map(item => ({
    date: item.date,
    hour: item.hour,
    rate: parseFloat((1.0 + Math.random() * 0.5).toFixed(2))
  }));
};
// Generate previous dispute activity data
export const generatePreviousDisputeActivityData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  let daysToShift = 7;
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      rate: parseFloat((item.rate * (0.9 + Math.random() * 0.2)).toFixed(2)),
      isPrevious: true
    };
  });
};
// Generate refund rate data
export const generateRefundRateData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  const refundData = generateRefundData(timeRange, startDate, endDate, userGranularity);
  const paymentData = generatePaymentVolumeData(timeRange, startDate, endDate, userGranularity);
  return refundData.map((item, i) => ({
    date: item.date,
    hour: item.hour,
    rate: parseFloat((item.amount / (paymentData[i]?.amount || 1) * 100).toFixed(2))
  }));
};
// Generate previous refund rate data
export const generatePreviousRefundRateData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  let daysToShift = 7;
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      rate: parseFloat((item.rate * (0.95 + Math.random() * 0.15)).toFixed(2)),
      isPrevious: true
    };
  });
};
// Generate new customers data
export const generateNewCustomersData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  if (startDate && endDate) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map(dateInfo => ({
      date: dateInfo.date,
      hour: dateInfo.hour,
      count: Math.floor(Math.random() * 50) + 30
    }));
  }
  const paymentData = generatePaymentVolumeData(timeRange, startDate, endDate, userGranularity);
  return paymentData.map(item => ({
    date: item.date,
    hour: item.hour,
    count: Math.floor(Math.random() * 50) + 30
  }));
};
// Generate previous new customers data
export const generatePreviousNewCustomersData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  let daysToShift = 7;
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      count: Math.floor(item.count * (0.8 + Math.random() * 0.25)),
      isPrevious: true
    };
  });
};
// Generate blocked payments count data
export const generateBlockedPaymentsData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  if (startDate && endDate) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map(dateInfo => ({
      date: dateInfo.date,
      hour: dateInfo.hour,
      count: Math.floor(Math.random() * 15) + 5
    }));
  }
  const paymentData = generatePaymentVolumeData(timeRange, startDate, endDate, userGranularity);
  return paymentData.map(item => ({
    date: item.date,
    hour: item.hour,
    count: Math.floor(Math.random() * 15) + 5
  }));
};
// Generate previous blocked payments data
export const generatePreviousBlockedPaymentsData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  let daysToShift = 7;
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      count: Math.floor(item.count * (0.75 + Math.random() * 0.3)),
      isPrevious: true
    };
  });
};
// Generate blocked payments volume data
export const generateBlockedPaymentsVolumeData = (timeRange: string, startDate?: Date, endDate?: Date, userGranularity?: string) => {
  if (startDate && endDate) {
    const granularity = determineGranularity(startDate, endDate, userGranularity);
    const dateRange = generateDateRange(startDate, endDate, granularity);
    return dateRange.map(dateInfo => ({
      date: dateInfo.date,
      hour: dateInfo.hour,
      amount: Math.floor(Math.random() * 5000) + 1500
    }));
  }
  const paymentData = generatePaymentVolumeData(timeRange, startDate, endDate, userGranularity);
  return paymentData.map(item => ({
    date: item.date,
    hour: item.hour,
    amount: Math.floor(Math.random() * 5000) + 1500
  }));
};
// Generate previous blocked payments volume data
export const generatePreviousBlockedPaymentsVolumeData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  let daysToShift = 7;
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      amount: Math.floor(item.amount * (0.75 + Math.random() * 0.3)),
      isPrevious: true
    };
  });
};
// Generate previous average transaction data
export const generatePreviousAverageTransactionData = (currentData: any[], timeRange: string) => {
  if (!currentData || currentData.length === 0) return [];
  let daysToShift = 7;
  if (timeRange === 'today') {
    daysToShift = 1;
  } else if (timeRange === '7d') {
    daysToShift = 7;
  } else if (timeRange === '30d') {
    daysToShift = 30;
  } else if (timeRange === '1y') {
    daysToShift = 365;
  } else {
    const firstDate = new Date(currentData[0].date);
    const lastDate = new Date(currentData[currentData.length - 1].date);
    const diffTime = Math.abs(lastDate.getTime() - firstDate.getTime());
    daysToShift = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  return currentData.map(item => {
    const date = new Date(item.date);
    date.setDate(date.getDate() - daysToShift);
    return {
      ...item,
      date: date.toISOString().split('T')[0],
      amount: Math.floor(item.amount * (0.92 + Math.random() * 0.1)),
      isPrevious: true
    };
  });
};